import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import subscriptionAddOnService from '../services/subscriptionAddOnService';
import { AddOnType } from '../types/subscription';

// Custom event name for AI Analytics subscription changes
export const AI_ANALYTICS_CHANGED_EVENT = 'aiAnalyticsSubscriptionChanged';

// Helper function to dispatch the event when subscription changes
export const dispatchAIAnalyticsChanged = () => {
  window.dispatchEvent(new CustomEvent(AI_ANALYTICS_CHANGED_EVENT));
};

/**
 * Custom hook to check if the current user has access to AI Analytics add-on
 * Automatically refreshes when subscription changes via custom event
 * @returns {boolean} true if user has active AI Analytics add-on, false otherwise
 */
export const useAIAnalyticsAccess = (): boolean => {
  const { state } = useAuth();
  const [hasAIAnalytics, setHasAIAnalytics] = useState(false);

  const checkAIAnalyticsAccess = useCallback(async () => {
    // Only check for Barber and ShopOwner roles
    if (!state.isAuthenticated || (state.user?.role !== 'Barber' && state.user?.role !== 'ShopOwner')) {
      setHasAIAnalytics(false);
      return;
    }

    try {
      // Small delay to ensure backend has processed the change
      // This helps avoid race conditions when called immediately after activation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Fetch available add-ons (which includes subscription status)
      const addOns = await subscriptionAddOnService.getAvailableAddOns();

      // Check if AI Analytics add-on is subscribed
      const aiAnalytics = addOns.find(addOn => addOn.type === AddOnType.AIAnalytics);
      const hasActiveAIAnalytics = aiAnalytics?.isSubscribed === true;

      setHasAIAnalytics(hasActiveAIAnalytics);
    } catch (error) {
      console.error('Error checking AI Analytics access:', error);
      setHasAIAnalytics(false);
    }
  }, [state.isAuthenticated, state.user?.role]);

  // Initial check on mount and when auth state changes
  useEffect(() => {
    checkAIAnalyticsAccess();
  }, [checkAIAnalyticsAccess]);

  // Listen for subscription change events
  useEffect(() => {
    const handleSubscriptionChange = () => {
      checkAIAnalyticsAccess();
    };

    window.addEventListener(AI_ANALYTICS_CHANGED_EVENT, handleSubscriptionChange);

    return () => {
      window.removeEventListener(AI_ANALYTICS_CHANGED_EVENT, handleSubscriptionChange);
    };
  }, [checkAIAnalyticsAccess]);

  return hasAIAnalytics;
};
